import { apiDelete, apiGet, apiPatchResult, apiPost, apiPostResult } from '@/services/api/http'
import { apiClient } from '@/services/api/axios'
import type { Chat, ChatListItem, ChatMessage, ChatUserRef, CursorPage } from '@/modules/messenger/types/messenger.types'
import { normalizeVoiceMimeType } from '@/modules/tasks/utils/voice-format'

export interface ListChatsParams {
  search?: string
  unread?: boolean
  kind?: 'DIRECT' | 'GROUP'
  cursor?: string
  pageSize?: number
}

export interface ListMessagesParams {
  cursor?: string
  pageSize?: number
}

export interface SearchMessagesParams {
  q: string
  cursor?: string
  pageSize?: number
}

export interface SendMessageInput {
  content: string
  parentMessageId?: string | null
  clientVoiceId?: string | null
  voiceDurationSec?: number | null
  voiceWaveform?: number[] | null
  /** @mentions typed into the composer — filtered server-side to this
   * chat's own active members (see messengerService.sendMessage). */
  mentionIds?: string[]
  /** Set by ForwardMessageDialog when re-sending via Forward — never set by
   * the normal composer. */
  forwarded?: boolean
}

export const messengerService = {
  /** Active staff (minus me) for "start a chat" / "add member" pickers. */
  listStaff: async (): Promise<ChatUserRef[]> => apiGet<ChatUserRef[]>('/chats/staff'),

  listChats: async (params: ListChatsParams = {}): Promise<CursorPage<ChatListItem>> =>
    apiGet<CursorPage<ChatListItem>>('/chats', { params }),

  unreadCount: async (): Promise<{ unreadCount: number }> =>
    apiGet<{ unreadCount: number }>('/chats/unread-count'),

  openDirect: async (userId: string): Promise<Chat> => {
    const { data } = await apiPostResult<Chat>('/chats/direct', { userId })
    return data
  },

  createGroup: async (name: string, memberIds: string[]): Promise<Chat> => {
    const { data } = await apiPostResult<Chat>('/chats', { name: name.trim(), memberIds })
    return data
  },

  getChat: async (chatId: string): Promise<Chat> => apiGet<Chat>(`/chats/${chatId}`),

  updateChat: async (chatId: string, name: string): Promise<Chat> => {
    const { data } = await apiPatchResult<Chat>(`/chats/${chatId}`, { name: name.trim() })
    return data
  },

  addMembers: async (chatId: string, memberIds: string[]): Promise<Chat> => {
    const { data } = await apiPostResult<Chat>(`/chats/${chatId}/members`, { memberIds })
    return data
  },

  removeMember: async (chatId: string, userId: string): Promise<Chat> =>
    apiDelete<Chat>(`/chats/${chatId}/members/${userId}`),

  /** Timestamp-only on the caller's own membership — never deletes
   * ChatMessage rows (see messengerService.js's clearChat). */
  clearChat: async (chatId: string): Promise<Chat> => {
    const { data } = await apiPostResult<Chat>(`/chats/${chatId}/clear`, {})
    return data
  },

  /** Hides this chat from the caller's own inbox — still a member, chat
   * reappears automatically the moment a new message lands (see
   * messengerService.js's deleteChat). */
  deleteChat: async (chatId: string): Promise<void> => {
    await apiPost<void>(`/chats/${chatId}/delete`, {})
  },

  listMessages: async (chatId: string, params: ListMessagesParams = {}): Promise<CursorPage<ChatMessage>> =>
    apiGet<CursorPage<ChatMessage>>(`/chats/${chatId}/messages`, { params }),

  /** Dedicated search endpoint — newest match first, not the reverse-cursor
   * shape listMessages uses for the live thread. */
  searchMessages: async (chatId: string, params: SearchMessagesParams): Promise<CursorPage<ChatMessage>> =>
    apiGet<CursorPage<ChatMessage>>(`/chats/${chatId}/messages/search`, { params }),

  /** Jump-to-result fallback when a match isn't already in the thread's
   * loaded infinite-query pages. */
  getMessage: async (chatId: string, messageId: string): Promise<ChatMessage> =>
    apiGet<ChatMessage>(`/chats/${chatId}/messages/${messageId}`),

  sendMessage: async (chatId: string, input: SendMessageInput): Promise<ChatMessage> => {
    const { data } = await apiPostResult<ChatMessage>(`/chats/${chatId}/messages`, {
      content: input.content.trim(),
      parentMessageId: input.parentMessageId ?? undefined,
      clientVoiceId: input.clientVoiceId ?? undefined,
      voiceDurationSec: input.voiceDurationSec ?? undefined,
      voiceWaveform: input.voiceWaveform ?? undefined,
      mentionIds: input.mentionIds?.length ? input.mentionIds : undefined,
      forwarded: input.forwarded ?? undefined,
    })
    return data
  },

  deleteMessage: async (chatId: string, messageId: string, forEveryone: boolean): Promise<void> => {
    await apiDelete(`/chats/${chatId}/messages/${messageId}`, { params: { forEveryone } })
  },

  markSeen: async (chatId: string, messageIds: string[]): Promise<void> => {
    if (!messageIds.length) return
    await apiPostResult(`/chats/${chatId}/messages/seen`, { messageIds })
  },

  /** Device got the message over WS (chat open or not) — double grey tick for sender. */
  markDelivered: async (chatId: string, messageIds: string[]): Promise<void> => {
    if (!messageIds.length) return
    await apiPostResult(`/chats/${chatId}/messages/delivered`, { messageIds })
  },

  toggleReaction: async (chatId: string, messageId: string, emoji: string): Promise<ChatMessage> => {
    const { data } = await apiPostResult<ChatMessage>(`/chats/${chatId}/messages/${messageId}/reactions`, { emoji })
    return data
  },

  /** Second step of send: the stub message (from `sendMessage`) already
   * exists — this attaches the actual audio. FormData must not keep the
   * axios default `application/json` Content-Type (see axios interceptor). */
  uploadMessageVoice: async (
    chatId: string,
    messageId: string,
    blob: Blob,
    mimeType: string,
  ): Promise<ChatMessage> => {
    const type = normalizeVoiceMimeType(mimeType || blob.type || 'audio/webm')
    const ext = type === 'audio/ogg' ? 'ogg' : type === 'audio/mp4' ? 'm4a' : 'webm'
    const file = typeof File !== 'undefined' ? new File([blob], `voice.${ext}`, { type }) : blob
    const form = new FormData()
    form.append('file', file, `voice.${ext}`)
    const { data } = await apiPostResult<ChatMessage>(`/chats/${chatId}/messages/${messageId}/voice`, form)
    return data
  },

  /** Binary stream, not a JSON envelope — bypasses http.ts's envelope
   * unwrapping on purpose (same carve-out as task.service.ts's
   * fetchCommentVoiceBlob). */
  fetchMessageVoiceBlob: async (
    chatId: string,
    messageId: string,
  ): Promise<{ blob: Blob; mimeType: string }> => {
    const response = await apiClient.get(`/chats/${chatId}/messages/${messageId}/voice`, {
      responseType: 'blob',
      validateStatus: () => true,
    })
    const mimeType = (String(response.headers['content-type'] || '').split(';')[0] ?? '').trim()
    const blob = response.data as Blob
    if (response.status < 200 || response.status >= 300) {
      const err = new Error(`Voice fetch failed (${response.status})`) as Error & {
        response?: { status: number }
      }
      err.response = { status: response.status }
      throw err
    }
    if (mimeType.includes('application/json') || mimeType.includes('text/')) {
      const err = new Error('Voice fetch returned a non-audio payload') as Error & {
        response?: { status: number }
      }
      err.response = { status: 404 }
      throw err
    }
    return { blob, mimeType: mimeType || blob.type || 'audio/webm' }
  },

  /** Listener pressed play — marks the note opened for this viewer. */
  markVoiceOpened: async (chatId: string, messageId: string): Promise<void> => {
    await apiPostResult(`/chats/${chatId}/messages/${messageId}/voice-opened`, {})
  },
}
